import type { Graphics } from 'pixi.js';
import type { TrailExtra } from '../../data/full/festival';
import type { RngState } from '../../shared/prng';
import type { Vec2 } from '../../sim/state';
import { parseHexColor } from '../palette';
import {
  CAIRNS,
  HOUSE,
  JETTY,
  type Way,
  at,
  jettyAlong,
  shoreAt,
  trailAt,
} from '../../data/full/path-layout';
import { LAKE, STONE } from './path-trail';
import { drawCrown } from './path-trees';
import { PATH_COLORS, towardTrail } from './path-way';

const ROOF = { tiles: parseHexColor('#35605e'), ridge: parseHexColor('#1f3d3c') };
const TORII = {
  beam: parseHexColor('#c8363a'),
  top: parseHexColor('#d0393a'),
  dark: parseHexColor('#2b1010'),
};
const SPIRIT = { body: parseHexColor('#f6f2ff'), edge: parseHexColor('#9aa8d8') };
const WOOD = {
  plank: parseHexColor('#6a4632'),
  dark: parseHexColor('#5e3e2c'),
  rope: parseHexColor('#c49a5c'),
};
const ROCK = { stone: parseHexColor('#5a6460'), edge: parseHexColor('#2a302e') };
const SMOKE = parseHexColor('#ffffff');

type TrailDrawing = (g: Graphics, way: Way, lights: readonly number[], rng: RngState) => Vec2[];

function house(g: Graphics, way: Way, along: number, lights: readonly number[]): Vec2[] {
  const centre = at(way, along, HOUSE.across);
  const turn = towardTrail(way, -1);
  const [cos, sin] = [Math.cos(turn), Math.sin(turn)];
  const world = (x: number, y: number) => ({
    x: centre.x + x * cos - y * sin,
    y: centre.y + x * sin + y * cos,
  });
  const outline = [
    [-92, -44],
    [-76, -40],
    [76, -40],
    [92, -44],
    [86, 52],
    [76, 48],
    [-76, 48],
    [-86, 52],
  ].flatMap(([x = 0, y = 0]) => {
    const p = world(x, y);
    return [p.x, p.y];
  });
  const shadow = outline.map((value, index) => value + (index % 2 === 0 ? 10 : 12));
  g.poly(shadow).fill({ color: PATH_COLORS.shade, alpha: 0.5 });
  g.poly(outline).fill(ROOF.tiles).stroke({ width: 3, color: PATH_COLORS.canopyEdge });
  for (let k = -60; k <= 60; k += 15) {
    const [a, b] = [world(k, -38), world(k, 46)];
    g.moveTo(a.x, a.y).lineTo(b.x, b.y);
  }
  g.stroke({ width: 1.5, color: ROOF.ridge, alpha: 0.6 });
  const [left, right] = [world(-76, 4), world(76, 4)];
  g.moveTo(left.x, left.y).lineTo(right.x, right.y).stroke({ width: 5, color: ROOF.ridge });
  const chimney = world(48, -22);
  g.rect(chimney.x - 8, chimney.y - 8, 16, 16)
    .fill(WOOD.dark)
    .stroke({ width: 2, color: PATH_COLORS.canopyEdge });
  g.moveTo(chimney.x, chimney.y)
    .bezierCurveTo(
      chimney.x + 10,
      chimney.y - 14,
      chimney.x - 6,
      chimney.y - 20,
      chimney.x + 4,
      chimney.y - 34,
    )
    .stroke({ width: 7, color: SMOKE, alpha: 0.3, cap: 'round' });
  return [-60, -20, 20, 60].map((x, index) => {
    const lamp = world(x, 58);
    g.roundRect(lamp.x - 6, lamp.y - 7, 12, 14, 5)
      .fill(lights[index % lights.length] ?? PATH_COLORS.glow)
      .stroke({ width: 1.5, color: TORII.dark });
    return lamp;
  });
}

const houses: TrailDrawing = (g, way, lights) =>
  HOUSE.shares.flatMap((share) => house(g, way, way.length * share, lights));

const islet: TrailDrawing = (g, way, _lights, rng) => {
  const along = way.length * 0.75;
  const { x, y } = at(way, along, shoreAt(way, along) + 100);
  g.ellipse(x, y, 74, 40).stroke({ width: 2, color: LAKE.ripple, alpha: 0.18 });
  g.moveTo(x - 58, y + 6)
    .bezierCurveTo(x - 50, y - 30, x - 10, y - 36, x + 20, y - 30)
    .bezierCurveTo(x + 56, y - 24, x + 66, y, x + 52, y + 20)
    .bezierCurveTo(x + 30, y + 38, x - 40, y + 36, x - 58, y + 6)
    .fill(ROCK.stone)
    .stroke({ width: 3, color: ROCK.edge });
  for (const [dx, dy, r] of [
    [-8, -10, 30],
    [16, -20, 22],
    [-26, -22, 18],
  ] as const) {
    drawCrown(g, x + dx, y + dy, r, 2, -Math.PI / 2, rng);
  }
  return [];
};

const torii: TrailDrawing = (g, way) => {
  const along = way.length * 0.4;
  const { x, y } = at(way, along, shoreAt(way, along) + 46);
  for (const post of [-40, 40]) {
    for (const r of [14, 24]) {
      g.ellipse(x + post, y + 48, r * 1.3, r * 0.45).stroke({
        width: 2,
        color: LAKE.ripple,
        alpha: 0.35,
      });
    }
    g.rect(x + post - 6, y - 8, 12, 56)
      .fill(TORII.beam)
      .stroke({ width: 2.5, color: TORII.dark });
  }
  g.rect(x - 58, y + 6, 116, 8)
    .fill(TORII.beam)
    .stroke({ width: 2, color: TORII.dark });
  g.moveTo(x - 74, y - 18)
    .quadraticCurveTo(x, y - 8, x + 74, y - 18)
    .lineTo(x + 72, y - 6)
    .quadraticCurveTo(x, y + 2, x - 72, y - 6)
    .closePath()
    .fill(TORII.top)
    .stroke({ width: 2.5, color: TORII.dark });
  g.moveTo(x - 76, y - 19)
    .quadraticCurveTo(x, y - 10, x + 76, y - 19)
    .stroke({ width: 5, color: PATH_COLORS.outline, cap: 'round' });
  return [];
};

const spirits: TrailDrawing = (g, way) => {
  for (const [share, out, flip] of [
    [0.3, 60, 1],
    [0.55, 110, -1],
    [0.86, 80, 1],
  ] as const) {
    const along = way.length * share;
    const { x, y } = at(way, along, shoreAt(way, along) + out);
    for (const r of [16, 26, 36]) {
      g.ellipse(x, y, r * 1.4, r * 0.7).stroke({
        width: 2,
        color: LAKE.ripple,
        alpha: 0.4 - r / 120,
      });
    }
    g.ellipse(x, y, 11, 7).fill(SPIRIT.body).stroke({ width: 1.5, color: SPIRIT.edge });
    for (const ear of [-9, -2]) {
      g.ellipse(x + ear * flip, y - 10, 3, 8)
        .fill(SPIRIT.body)
        .stroke({ width: 1.2, color: SPIRIT.edge });
    }
  }
  return [];
};

const jetty: TrailDrawing = (g, way) => {
  const along = jettyAlong(way);
  const turn = towardTrail(way, 1);
  const [cos, sin] = [Math.cos(turn), Math.sin(turn)];
  const base = at(way, along, shoreAt(way, along) - 6);
  const world = (x: number, y: number) => ({
    x: base.x + x * cos - y * sin,
    y: base.y + x * sin + y * cos,
  });
  const quad = (x: number, y: number, w: number, h: number) =>
    [world(x, y), world(x + w, y), world(x + w, y + h), world(x, y + h)].flatMap((p) => [p.x, p.y]);
  const planks = 6;
  const step = JETTY.reach / planks;
  for (let k = 0; k < planks; k++) {
    g.poly(quad(-JETTY.half, -(k + 1) * step + 2, 2 * JETTY.half, step - 2))
      .fill(k % 2 === 0 ? WOOD.plank : WOOD.dark)
      .stroke({ width: 2, color: PATH_COLORS.plankEdge });
  }
  const boat = world(70, -72);
  g.ellipse(boat.x, boat.y, way.down ? 14 : 30, way.down ? 30 : 14)
    .fill(WOOD.plank)
    .stroke({ width: 2.5, color: PATH_COLORS.plankEdge });
  const knot = world(24, -80);
  g.moveTo(knot.x, knot.y).lineTo(boat.x, boat.y).stroke({ width: 1.5, color: WOOD.rope });
  return [];
};

const cairns: TrailDrawing = (g, way) => {
  for (const [share, out, stones] of CAIRNS) {
    const along = way.length * share;
    const { x, y } = at(way, along, trailAt(way, along) + out);
    for (let k = 0; k < stones; k++) {
      g.circle(x + k, y - k * 2, 14 - k * 3)
        .fill(k % 2 === 0 ? STONE.base : STONE.cap)
        .stroke({ width: 2, color: STONE.edge });
    }
  }
  return [];
};

export const TRAIL_DRAWINGS: Readonly<Record<TrailExtra, TrailDrawing>> = {
  houses,
  islet,
  torii,
  spirits,
  jetty,
  cairns,
};
